import {
  endOfDay,
  startOfDay,
  parseISO,
  format,
  startOfWeek,
  addDays,
} from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";

/**
 * Get the client's timezone
 */
export function getClientTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format a date string or Date object in the client's timezone
 */
export function formatInClientTimezone(
  date: string | Date,
  formatStr: string = "MMMM d",
): string {
  const timezone = getClientTimezone();
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  return formatInTimeZone(dateObj, timezone, formatStr);
}

// Locale-aware date formatting using Intl.DateTimeFormat
export function formatDateTimeLocale(
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  },
): string {
  const timezone = getClientTimezone();
  const dateObj = typeof date === "string" ? parseISO(date) : date;

  return new Intl.DateTimeFormat(undefined, {
    ...options,
    timeZone: timezone,
  }).format(dateObj);
}

/**
 * Get the short timezone abbreviation for the client's timezone (e.g. "EST",
 * "PST", "GMT+2"). The abbreviation is DST-dependent, so pass the relevant
 * session date to get the correct value for that moment.
 */
export function getClientTimezoneAbbr(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: getClientTimezone(),
    timeZoneName: "short",
  }).formatToParts(date);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

/**
 * Format the time-of-day in the client's timezone with the zone abbreviation
 * appended for reassurance, e.g. "2:00 PM EST".
 */
export function formatTimeWithZone(date: string | Date): string {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  const abbr = getClientTimezoneAbbr(dateObj);
  const time = formatTimeOnly(dateObj);
  return abbr ? `${time} ${abbr}` : time;
}

/**
 * Format a full date + time in the client's timezone with the zone
 * abbreviation appended, e.g. "Dec 31, 2:00 PM EST".
 */
export function formatDateTimeWithZone(date: string | Date): string {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  const abbr = getClientTimezoneAbbr(dateObj);
  const dateTime = formatDateTime(dateObj);
  return abbr ? `${dateTime} ${abbr}` : dateTime;
}

// Convenient preset functions for common formats
export function formatDateOnly(date: string | Date): string {
  return formatDateTimeLocale(date, { dateStyle: "medium" });
}

/** "Aug 3 – Sep 28, 2026": one year when both dates share it. */
export function formatDateRange(
  start: string | Date,
  end: string | Date,
): string {
  const toDate = (date: string | Date) =>
    typeof date === "string" ? parseISO(date) : date;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: getClientTimezone(),
  }).formatRange(toDate(start), toDate(end));
}

export function formatDateTime(date: string | Date): string {
  return formatDateTimeLocale(date, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatDateTimeLong(date: string | Date): string {
  return formatDateTimeLocale(date, { dateStyle: "full", timeStyle: "medium" });
}

// Specific preset functions for common use cases
export function formatTimeOnly(date: string | Date): string {
  return formatDateTimeLocale(date, { timeStyle: "short" });
}

export function formatDateShort(date: string | Date): string {
  return formatDateTimeLocale(date, { dateStyle: "short" });
}

export function formatMonthDay(date: string | Date): string {
  return formatDateTimeLocale(date, { month: "short", day: "numeric" });
}

/** Formats date as "Mon DD" if current year, otherwise "Mon DD, YYYY". */
export function formatDateSmart(date: string | Date): string {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  const currentYear = new Date().getFullYear();
  const dateYear = dateObj.getFullYear();

  if (dateYear === currentYear) {
    return formatDateTimeLocale(date, { month: "short", day: "numeric" });
  }
  return formatDateTimeLocale(date, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatWeekdayMonthDay(date: string | Date): string {
  return formatDateTimeLocale(date, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Get current time in client's timezone
 */
export function getCurrentTimeInClientTimezone(): Date {
  const timezone = getClientTimezone();
  return toZonedTime(new Date(), timezone);
}

/**
 * Check if a date is overdue (past current time in client's timezone)
 */
export function isOverdueInClientTimezone(date: string | Date): boolean {
  const timezone = getClientTimezone();
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  const now = getCurrentTimeInClientTimezone();
  const zonedDate = toZonedTime(dateObj, timezone);
  return zonedDate < now;
}

/**
 * Get localized short day names (Su, Mo, Tu, etc.) starting from locale's first day of week
 */
export function getShortDayNames(): string[] {
  const days = [];
  // Use a reference date and get the start of week according to locale
  const referenceDate = new Date(2024, 0, 7); // January 7, 2024 was a Sunday
  const weekStart = startOfWeek(referenceDate);

  for (let i = 0; i < 7; i++) {
    const date = addDays(weekStart, i);
    days.push(format(date, "EEEEEE")); // EEEEEE gives short day names like Su, Mo, Tu
  }
  return days;
}

/**
 * Get localized month names
 */
export function getMonthNames(): string[] {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date(2024, i, 1);
    months.push(format(date, "MMMM")); // MMMM gives full month names
  }
  return months;
}

/**
 * Format date for display with timezone consideration
 * @deprecated Use formatDateOnly() instead for locale-aware formatting
 */
export function formatDateForDisplay(date: string | Date): string {
  return formatDateOnly(date);
}

/**
 * Format date for form display (full date)
 * @deprecated Use formatDateOnly() instead for locale-aware formatting
 */
export function formatDateForForm(date: string | Date): string {
  return formatDateOnly(date);
}

/**
 * Get hour of day in client's timezone (for greetings)
 */
export function getHourInClientTimezone(): number {
  const now = getCurrentTimeInClientTimezone();
  return now.getHours();
}

export const getDaysInMonth = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();

  // Use locale-aware start of week
  const weekStart = startOfWeek(firstDay);
  const daysDiff = Math.floor(
    (firstDay.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24),
  );

  const days = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < daysDiff; i++) {
    days.push(null);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(year, month, day));
  }

  return days;
};

export const formatRelativeDate = (date: Date) => {
  const today = getCurrentTimeInClientTimezone();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  }
  if (date.toDateString() === tomorrow.toDateString()) {
    return "Tomorrow";
  }

  return format(date, "EEE, d MMM");
};

export const getCurrentWeekDays = (currentDate: Date) => {
  const today = getCurrentTimeInClientTimezone();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  // Check if we're viewing the current month
  const isCurrentMonth =
    currentDate.getMonth() === currentMonth &&
    currentDate.getFullYear() === currentYear;

  if (isCurrentMonth) {
    // Show current week for current month using locale-aware start of week
    const currentWeekStart = startOfWeek(today);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      weekDays.push(addDays(currentWeekStart, i));
    }
    return weekDays;
  } else {
    // Show first week of the month for other months
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    );
    const firstWeekStart = startOfWeek(firstDayOfMonth);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      weekDays.push(addDays(firstWeekStart, i));
    }
    return weekDays;
  }
};

export function assertFutureDeadline(d?: Date) {
  if (!d) return;
  const now = new Date();
  if (d.getTime() <= now.getTime()) {
    throw new Error("Deadline must be in the future");
  }
}

/**
 * Get start of today in client's timezone.
 * Useful for date comparisons in calendar pickers.
 */
export function getStartOfTodayInClientTimezone(): Date {
  const now = getCurrentTimeInClientTimezone();
  return startOfDay(now);
}

/**
 * Get end of today in client's timezone (23:59:59.999).
 * Useful for setting default due dates.
 */
export function getEndOfTodayInClientTimezone(): Date {
  const now = getCurrentTimeInClientTimezone();
  return endOfDay(now);
}
